import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from 'utils/shadcn';

const buttonVariants = cva(
  'inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default:
          'bg-primary text-primary-foreground shadow hover:bg-primary/90',
        destructive:
          'bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90',
        outline:
          'border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground dark:bg-dark-100 dark:border-none dark:text-white dark:hover:bg-accent dark:hover:text-accent-foreground !px-4',
        dashed:
          'border py-1 border-dashed border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground text-xs',
        secondary:
          'bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
        novice: 'bg-black-500 a text-white shadow-sm hover:bg-orange-600',
				magenta: 'bg-button-magenta text-white shadow-sm hover:bg-button-magenta-hover',
        experienced: 'bg-blue-200 d text-white shadow-sm hover:bg-green-300',
        expert: 'bg-green-500  text-white shadow-sm hover:bg-green-600',
        badge:
          'bg-primary rounded-sm text-xs text-white shadow-sm hover:bg-green-600 !h-6 py-2 !px-[5px]',
        chips:
          'bg-[#F4F4F5] rounded-sm text-[11px] shadow-sm !h-6 py-2 !px-[5px] mb-2',
      },
      size: {
        default: 'h-9 px-4 py-2',
        sm: 'h-8 rounded-md px-3 text-xs',
        lg: 'h-10 rounded-md px-8',
        icon: 'h-9 w-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
  VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, type = "button", ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        type={type}
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
